import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DeliveryNote, LineSuggestions } from '../models/delivery-note.models';

/** Delivery notes API (logixi-be delivery-note module). Lists go through app-grid; these are the reads and writes. */
@Injectable({
  providedIn: 'root'
})
export class DeliveryNoteService {
  private readonly endpoint = environment.api.endpoint + 'delivery-note';

  constructor(private http: HttpClient) { }

  get(id: number): Observable<DeliveryNote> {
    return this.http.get<DeliveryNote>(`${this.endpoint}/${id}`);
  }

  create(note: DeliveryNote): Observable<DeliveryNote> {
    return this.http.post<DeliveryNote>(this.endpoint, note);
  }

  update(id: number, note: Partial<DeliveryNote>): Observable<DeliveryNote> {
    return this.http.patch<DeliveryNote>(`${this.endpoint}/${id}`, note);
  }

  /** ready | draft | cancel | despatch */
  action(id: number, action: string): Observable<DeliveryNote> {
    return this.http.post<DeliveryNote>(`${this.endpoint}/${id}/${action}`, {});
  }

  suggestions(id: number): Observable<LineSuggestions[]> {
    return this.http.get<LineSuggestions[]>(`${this.endpoint}/${id}/suggestions`);
  }

  pick(id: number, clientRef: string, picks: { lineId: number; warehouseLocationId: number; batchNumber?: string; qty: number }[]): Observable<DeliveryNote> {
    return this.http.post<DeliveryNote>(`${this.endpoint}/${id}/pick`, { clientRef, picks });
  }

  /** Writes the PDF and gives back its public address. */
  pdf(id: number, kind: 'delivery-note' | 'picking-slip'): Observable<string> {
    return this.http.post<{ fileName: string }>(`${this.endpoint}/${id}/pdf/${kind}`, { pdfSettings: environment.invoicing })
      .pipe(map((r) => environment.publicUrl.replace(/\/$/, '') + r.fileName));
  }

  accounts(): Observable<{ id: number; name: string }[]> {
    return this.http.get<any[]>(environment.api.endpoint + 'account?fields=id,name,isactive&sort=name,ASC')
      .pipe(map((rows) => rows.filter((r) => r.isactive !== false && r.isactive !== 0)));
  }

  /** Active products, with their owning account and sell price. */
  products(): Observable<{ id: number; name: string; sku: string; accountId: number | null; price: number }[]> {
    return this.http.get<any[]>(environment.api.endpoint + 'product?fields=id,name,sku,accountId,price,active&filter=active||eq||1&sort=name,ASC');
  }

  locations(): Observable<{ id: number; label: string }[]> {
    return this.http.get<any[]>(environment.api.endpoint + 'warehouse-location?join=warehouse&sort=aisle,ASC&sort=shelf,ASC')
      .pipe(map((rows) => rows.map((l) => ({ id: l.id, label: `${l.aisle} / ${l.shelf}${l.warehouse ? ' (' + l.warehouse.name + ')' : ''}` }))));
  }
}
